import React, { useEffect, useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import './styles.css';

/*
 * How far up from the bottom of the viewport the dock has to start so it sits
 * above the footer instead of over it. In the authenticated shell the footer is
 * pinned to the bottom of a viewport-height layout, so the overlap is constant;
 * on the signed-out pages the footer only comes into view at the end of a
 * scroll, and the dock lifts to meet it.
 */
const useFooterLift = () => {
    const [lift, setLift] = useState(0);

    useEffect(() => {
        let frame = 0;

        const measure = () => {
            frame = 0;
            const footer = document.querySelector('footer');
            if (!footer) {
                setLift(0);
                return;
            }
            const { top } = footer.getBoundingClientRect();
            setLift(Math.max(0, Math.round(window.innerHeight - top)));
        };

        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(measure);
        };

        schedule();
        window.addEventListener('resize', schedule);
        // Capture phase: the authenticated shell scrolls an inner region, not
        // the window, and those events never reach a bubbling listener here.
        window.addEventListener('scroll', schedule, true);

        const footer = document.querySelector('footer');
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
        if (observer && footer) observer.observe(footer);

        return () => {
            if (frame) window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', schedule);
            window.removeEventListener('scroll', schedule, true);
            if (observer) observer.disconnect();
        };
    }, []);

    return lift;
};

/*
 * A chat that lives in the bottom-right corner instead of in the page flow.
 * The editor and the Idea step both had a full-width chat panel pushing the
 * work itself (the timeline, the script) below the fold — the conversation is
 * a side channel, so it collapses to a single button until it is wanted.
 */
function FloatingChatDock({
    open,
    onOpen,
    onClose,
    title,
    icon = <MessageSquare size={18} aria-hidden="true" />,
    launcherLabel = 'Ask AI',
    launcherHint = '',
    busy = false,
    // Something in the collapsed panel is waiting on the user (a question, an
    // offer). Distinct from `busy`, which means the agent is still thinking.
    attention = false,
    status = null,
    actions = null,
    footer = null,
    className = '',
    children,
}) {
    const lift = useFooterLift();
    // Consumed by the `bottom: calc(... + var(--floating-chat-lift))` rules, so
    // the gap stays whatever the breakpoint says it is.
    const liftStyle = { '--floating-chat-lift': `${lift}px` };

    if (!open) {
        return (
            <button
                type="button"
                className={`floating-chat-launcher ${busy ? 'busy' : ''} ${attention ? 'attention' : ''} ${className}`}
                style={liftStyle}
                onClick={onOpen}
                title={launcherHint || launcherLabel}
                aria-label={launcherHint || launcherLabel}
            >
                <span className="floating-chat-launcher-icon">{icon}</span>
                <span className="floating-chat-launcher-label">{launcherLabel}</span>
                {(busy || attention) && <span className="floating-chat-launcher-dot" aria-hidden="true" />}
            </button>
        );
    }

    return (
        <section className={`floating-chat-dock ${className}`} style={liftStyle} role="dialog" aria-label={title}>
            <header className="floating-chat-header">
                <span className="floating-chat-title">
                    <span aria-hidden="true">{icon}</span> {title}
                </span>
                <div className="floating-chat-header-right">
                    {status}
                    {actions}
                    <button
                        type="button"
                        className="floating-chat-close"
                        onClick={onClose}
                        title="Close chat"
                        aria-label="Close chat"
                    >
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>
            </header>
            <div className="floating-chat-body">{children}</div>
            {footer && <div className="floating-chat-footer">{footer}</div>}
        </section>
    );
}

export { FloatingChatDock };
export default FloatingChatDock;
